import { Component, Inject } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA } from '@angular/material';
import { FinanceService } from 'app/finance/finance.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

export interface TimelineMonthPeriodRow {
  type: 'statement' | 'gap';
  start: number; // timestamp (ms)
  end: number;   // timestamp (ms)
  label?: string; // statement filename / label
  statementId?: number; // only for statement rows
  // Gap "include" UI state + candidates
  include?: {
    isEditing: boolean;
    selectedTarget?: 'before' | 'after';
    selectedStatementId?: number;
    saving?: boolean;
  };
  includeCandidates?: Array<{
    target: 'before' | 'after';
    statementId: number;
    label: string;
  }>;
}

export interface TimelineMonthDetailsDialogData {
  clientName?: string;
  accountName?: string;
  accountNumber?: string;
  bankName?: string;
  monthLabel: string;
  periods: TimelineMonthPeriodRow[];
}

@Component({
  selector: 'app-timeline-month-details-dialog',
  templateUrl: './timeline-month-details-dialog.component.html',
  styleUrls: ['./timeline-month-details-dialog.component.css']
})
export class TimelineMonthDetailsDialogComponent {
  constructor(
    public dialogRef: MdDialogRef<TimelineMonthDetailsDialogComponent>,
    @Inject(MD_DIALOG_DATA) public data: TimelineMonthDetailsDialogData,
    private financeService: FinanceService,
    private snackBarService: SnackBarService
  ) {}

  close() {
    this.dialogRef.close();
  }

  startInclude(row: TimelineMonthPeriodRow) {
    if (!row || row.type !== 'gap') {
      return;
    }
    if (!row.include) {
      row.include = { isEditing: true };
    } else {
      row.include.isEditing = true;
    }
  }

  onIncludeSelection(row: TimelineMonthPeriodRow) {
    if (!row || !row.include) {
      return;
    }
    const candidates = row.includeCandidates || [];
    const found = candidates.find(c => c.statementId === row.include.selectedStatementId);
    row.include.selectedTarget = found ? found.target : undefined;
  }

  cancelInclude(row: TimelineMonthPeriodRow) {
    if (!row || row.type !== 'gap' || !row.include) {
      return;
    }
    row.include.isEditing = false;
    row.include.selectedTarget = undefined;
    row.include.selectedStatementId = undefined;
    row.include.saving = false;
  }

  saveInclude(row: TimelineMonthPeriodRow) {
    if (!row || row.type !== 'gap' || !row.include) {
      return;
    }
    const target = row.include.selectedTarget;
    const statementId = row.include.selectedStatementId;
    if (!target || !statementId) {
      this.snackBarService.showSimpleMsj('Please select a statement to include the gap into.');
      return;
    }

    // For "before": extend statement end date to gap end.
    // For "after": move statement start date to gap start.
    const startDate = target === 'after' ? row.start : null;
    const endDate = target === 'before' ? row.end : null;

    row.include.saving = true;
    this.financeService.updateBankStatementPeriod(statementId, startDate, endDate).subscribe(
      (response: any) => {
        row.include.saving = false;
        this.snackBarService.showSimpleMsj('Bank statement period updated.');
        // Refresh timeline view after closing
        this.dialogRef.close({ refreshTimeline: true });
      },
      (error: any) => {
        row.include.saving = false;
        this.snackBarService.showSimpleMsj('Failed to update bank statement period: ' + (error.message || 'Unknown error'));
      }
    );
  }

  formatDate(timestamp: number): string {
    if (!timestamp) {
      return '-';
    }
    return formatDisplayDate(timestamp) || '-';
  }
}




// WEBPACK FOOTER //
// ./src/app/finance/bank-statements-batch/timeline-month-details-dialog/timeline-month-details-dialog.component.ts